import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of, Subscription, switchMap } from 'rxjs';
import { CaasContentService } from '../../services/caas-content.service';
import { ImageService } from '../../services/image.service';
import { PodcastItems, PodcastPlatforms } from 'hdi-components/source';
import { ImageMetaData, MenuItem, ModalType, News } from 'hdi-components';
import { ModalVideoService } from '../../services/modal-video.service';
import { SafeResourceUrl } from '@angular/platform-browser';
import { NavigationService } from '../../services/navigation.service';
import {
  CaasComponent,
  CaasLayout,
  CaasMainSection,
  CaasRoutes,
  CaasStage,
} from '../../../../hdi-components/src/shared/interfaces/caas/caas.interface';

@Component({
  selector: 'app-standard-page',
  templateUrl: './standard-page.component.html',
  styleUrl: './standard-page.component.scss',
})
export class StandardPageComponent implements OnInit, OnDestroy {
  caasData: CaasLayout[] = [];
  mainSections?: CaasMainSection[];
  pageCaasSubscription$!: Subscription;
  showModal = false;
  videoId?: string;
  videoSrc?: SafeResourceUrl;
  podcastData: { podcasts: PodcastItems[]; platforms: PodcastPlatforms[] } = {
    podcasts: [],
    platforms: [],
  };
  news: News[] = [];
  newsComponent: CaasComponent | undefined;
  newsFilters: { filter: string; categories: string[]; identifier: string }[] =
    [];
  filteredNews: News[] = [];
  imagesMetaData: ImageMetaData[] = [];
  alternateBackground: ('white' | 'grey')[] = [];
  events: CaasLayout[] = [];
  ModalType = ModalType;
  breadcrumbs: { title: string; url?: string }[] = [];
  inpageMenu: MenuItem[] = [];
  tags: { name: string; identifier: string }[] = [];

  constructor(
    private caasContentService: CaasContentService,
    private routes: ActivatedRoute,
    private imageServices: ImageService,
    private modalVideoService: ModalVideoService,
    private navigationService: NavigationService
  ) {}

  ngOnInit() {
    this.openVideoModalByQueryParams();
    this.pageCaasSubscription$ = this.routes.data
      .pipe(
        switchMap((data) => {
          if (data?.['pageIdentifier']) {
            return forkJoin({
              content: this.caasContentService.getContent(
                data['pageIdentifier']
              ),
              podcastData: this.caasContentService.getPodcastData(),
              news: this.caasContentService.getNews(),
              newsFilters: this.caasContentService.getNewsCategories(),
              events: this.caasContentService.getEvents(),
              navData: this.navigationService.getNavigation(),
              imagesMetaData: this.caasContentService.getImagesMetaData(),
              tags: this.caasContentService.getTags(),
            });
          } else {
            return of({
              content: [],
              podcastData: { podcasts: [], platforms: [] },
              news: [],
              newsFilters: [],
              events: [],
              navData: [],
              imagesMetaData: [],
              tags: [],
            });
          }
        })
      )
      .subscribe(
        ({
          content,
          podcastData,
          news,
          newsFilters,
          events,
          navData,
          imagesMetaData,
          tags,
        }) => {
          this.caasData = content;
          // Main sections and handling the news component
          this.mainSections = (
            content.find((layout) => layout.name === 'main')
              ?.children as CaasMainSection[]
          )?.filter((section) => {
            if (
              section?.formData?.st_items?.value[0]?.template?.uid ===
                'st_news' &&
              section?.formData?.st_items?.value[0]?.formData?.st_layout?.value
                ?.identifier === 'hub'
            ) {
              this.newsComponent = section?.formData?.st_items?.value[0];
            }
            return (
              section?.formData?.st_items?.value[0]?.template?.uid !==
                'st_news' ||
              section?.formData?.st_items?.value[0]?.formData?.st_layout?.value
                ?.identifier !== 'hub'
            );
          });
          const stageLayout = content.find((layout) => layout.name === 'stage');
          if (stageLayout) {
            this.imageServices.replaceStageImages(
              stageLayout.children?.[0] as CaasStage
            );
          }

          if (this.mainSections) {
            this.imageServices.replaceMainImages(this.mainSections);
          }
          // console.log('Stage:', stageLayout?.children?.[0]);
          // console.log('Main:', this.mainSections);
          // console.log('PodcastData:', podcastData);
          this.podcastData = podcastData;
          this.news = news;
          this.newsFilters = newsFilters;
          this.tags = tags;
          this.imagesMetaData = imagesMetaData;
          this.getAlternateBackground();
          this.inpageMenu = this.getInpageNav();
          if (this.news) {
            this.imageServices.replaceNewsImages(
              this.filteredNews.length > 0 ? this.filteredNews : this.news
            );
          }
          if (this.podcastData) {
            this.imageServices.replacePodcastsImages(this.podcastData.podcasts);
          }
          this.events = events;
          if (navData) {
            this.setBreadcrumbs(navData);
          }
        }
      );
  }
  getInpageNav(): MenuItem[] {
    return (this.mainSections ?? []).reduce((acc, section) => {
      if (
        section.formData.st_anchor_name?.value &&
        section.formData.st_inpagenav?.value
      ) {
        acc.push({
          title:
            section.formData.st_inpagenav.value.formData.lt_link_label.value ??
            '',
          url: section.formData.st_anchor_name.value,
          hoverText:
            section.formData.st_inpagenav.value.formData.lt_link_title?.value ??
            '',
        });
      }
      if (
        section.formData.st_items &&
        section.formData.st_items.value &&
        section.formData.st_items.value.length > 0
      ) {
        section.formData.st_items.value.forEach((item) => {
          if (
            item.formData.st_anchor_name?.value &&
            item.formData.st_inpagenav?.value
          ) {
            acc.push({
              title:
                item.formData.st_inpagenav.value.formData.lt_link_label.value ??
                '',
              url: item.formData.st_anchor_name.value,
              hoverText:
                item.formData.st_inpagenav.value.formData.lt_link_title
                  ?.value ?? '',
            });
          }
        });
      }
      return acc;
    }, [] as MenuItem[]);
  }

  openVideoModalByQueryParams(): void {
    const { showModal, videoId, videoSrc } =
      this.modalVideoService.openModalVideo();
    this.showModal = showModal;
    this.videoId = videoId;
    this.videoSrc = videoSrc;
  }

  updateNews(filteredNews: News[]): void {
    this.filteredNews = filteredNews;
  }
  getAlternateBackground() {
    let isWhite = (index: number) => index % 2 !== 0;
    this.mainSections?.forEach((section, index) => {
      if (section.formData.st_background_reset?.value) {
        this.alternateBackground.push('white');
        if (!isWhite(index) && !isWhite(0)) {
          isWhite = (index: number) => index % 2 === 0;
        } else {
          isWhite = (index: number) => index % 2 !== 0;
        }
      } else if (isWhite(index)) {
        this.alternateBackground.push('white');
      } else {
        this.alternateBackground.push('grey');
      }
    });
  }
  private setBreadcrumbs(navData: CaasRoutes[]): void {
    const currentPath = this.routes.snapshot.url
      .map((segment) => segment.path)
      .join('/');
    this.breadcrumbs = this.navigationService.generateBreadcrumbs(
      `/${currentPath}`,
      navData
    );
  }

  ngOnDestroy(): void {
    this.pageCaasSubscription$.unsubscribe();
  }
}
